import React, { useCallback, useEffect, useMemo, useState } from 'react'
import Modal from 'react-bootstrap/Modal'
import { GetAction } from '../../Component/GetAction'
import { PostAction } from '../../Component/PostAction'
import { RequireModule, PageHeader, BTN_PRIMARY } from './shared'

const EMPTY = {
  menu_item_id: '',
  location: 'header',
  parent_id: '',
  label: '',
  url: '',
  sort_order: 0,
  status: 1,
  open_new_tab: 0,
}

function isTopLevel(row) {
  const pid = row?.parent_id
  return pid == null || pid === '' || Number(pid) === 0
}

function parentKeyOf(row) {
  return isTopLevel(row) ? null : Number(row.parent_id)
}

function sortByOrder(a, b) {
  return (
    (Number(a.sort_order) || 0) - (Number(b.sort_order) || 0) ||
    (Number(a.menu_item_id) || 0) - (Number(b.menu_item_id) || 0)
  )
}

/** Flat list ordered for display: each parent followed by its children. */
function flattenForDisplay(items) {
  const tops = items.filter(isTopLevel).sort(sortByOrder)
  const out = []
  for (const top of tops) {
    out.push(top)
    const kids = items
      .filter((r) => Number(r.parent_id) === Number(top.menu_item_id))
      .sort(sortByOrder)
    out.push(...kids)
  }
  const seen = new Set(out.map((r) => Number(r.menu_item_id)))
  for (const row of items) {
    if (!seen.has(Number(row.menu_item_id))) out.push(row)
  }
  return out
}

function countChildren(items, id) {
  return items.filter((r) => Number(r.parent_id) === Number(id)).length
}

function groupFor(items, parentId) {
  return items
    .filter((r) => parentKeyOf(r) === parentId)
    .sort(sortByOrder)
}

function reindexGroup(group, parentId) {
  return group.map((row, i) => ({
    ...row,
    sort_order: i + 1,
    parent_id: parentId,
  }))
}

/**
 * Apply a drop: nest under a top-level item (`into`) or insert as a
 * sibling before/after the target (same parent as the target).
 */
function applyMenuDrop(items, sourceId, targetId, zone) {
  if (!sourceId || !targetId || sourceId === targetId) return { error: null, next: null, changed: [] }

  const source = items.find((r) => Number(r.menu_item_id) === sourceId)
  const target = items.find((r) => Number(r.menu_item_id) === targetId)
  if (!source || !target) return { error: null, next: null, changed: [] }

  const sourceHasKids = countChildren(items, sourceId) > 0
  let newParent
  if (zone === 'into') {
    if (!isTopLevel(target)) {
      newParent = parentKeyOf(target)
    } else if (sourceHasKids) {
      return {
        error:
          'Move or remove child items before nesting this dropdown under another item.',
        next: null,
        changed: [],
      }
    } else {
      newParent = Number(target.menu_item_id)
    }
  } else {
    newParent = parentKeyOf(target)
  }

  if (newParent === sourceId) return { error: null, next: null, changed: [] }
  if (newParent != null && sourceHasKids) {
    return {
      error:
        'Move or remove child items before nesting this dropdown under another item.',
      next: null,
      changed: [],
    }
  }

  if (newParent != null) {
    const parentRow = items.find((r) => Number(r.menu_item_id) === newParent)
    if (parentRow && !isTopLevel(parentRow)) {
      newParent = parentKeyOf(parentRow)
    }
  }

  const oldParent = parentKeyOf(source)
  const others = items.filter((r) => Number(r.menu_item_id) !== sourceId)
  const moved = { ...source, parent_id: newParent }
  const destGroup = groupFor(others, newParent)

  let insertAt
  if (zone === 'into') {
    insertAt = destGroup.length
  } else {
    const tgtIdx = destGroup.findIndex((r) => Number(r.menu_item_id) === targetId)
    insertAt = tgtIdx < 0 ? destGroup.length : zone === 'before' ? tgtIdx : tgtIdx + 1
  }
  destGroup.splice(insertAt, 0, moved)

  const destOrdered = reindexGroup(destGroup, newParent)
  let changed = destOrdered
  if (oldParent !== newParent) {
    const oldOrdered = reindexGroup(groupFor(others, oldParent), oldParent)
    changed = [...oldOrdered, ...destOrdered]
  }

  const byId = new Map(changed.map((r) => [Number(r.menu_item_id), r]))
  const next = items.map((r) => byId.get(Number(r.menu_item_id)) || r)
  return { error: null, next, changed }
}

function MenuList({
  location,
  title,
  items,
  onEdit,
  onDelete,
  onReorder,
  savingOrder,
}) {
  const [dragId, setDragId] = useState(null)
  const [overId, setOverId] = useState(null)
  const [overZone, setOverZone] = useState(null)
  const displayItems = useMemo(() => flattenForDisplay(items), [items])
  const labelById = useMemo(() => {
    const map = {}
    items.forEach((r) => {
      map[Number(r.menu_item_id)] = r.label
    })
    return map
  }, [items])

  const zoneForTarget = (sourceId, targetRow, clientY, top, height) => {
    if (!sourceId || !targetRow || Number(targetRow.menu_item_id) === sourceId) {
      return null
    }
    const source = items.find((r) => Number(r.menu_item_id) === sourceId)
    if (!source) return null
    const sourceHasKids = countChildren(items, sourceId) > 0
    const canNest = isTopLevel(targetRow) && !sourceHasKids
    const ratio = height > 0 ? (clientY - top) / height : 0.5
    if (canNest && ratio >= 0.28 && ratio <= 0.72) return 'into'
    return ratio < 0.5 ? 'before' : 'after'
  }

  const applyDrop = async (sourceId, targetId, zone) => {
    const result = applyMenuDrop(items, sourceId, targetId, zone)
    if (result.error) {
      window.alert(result.error)
      return
    }
    if (!result.next || result.changed.length === 0) return
    await onReorder(location, result.next, result.changed)
  }

  const moveByOffset = async (row, offset) => {
    const parentId = parentKeyOf(row)
    const group = groupFor(items, parentId)
    const idx = group.findIndex((r) => Number(r.menu_item_id) === Number(row.menu_item_id))
    if (idx < 0) return
    const nextIdx = idx + offset
    if (nextIdx < 0 || nextIdx >= group.length) return
    const nextGroup = [...group]
    const [moved] = nextGroup.splice(idx, 1)
    nextGroup.splice(nextIdx, 0, moved)
    const changed = reindexGroup(nextGroup, parentId)
    const byId = new Map(changed.map((r) => [Number(r.menu_item_id), r]))
    const next = items.map((r) => byId.get(Number(r.menu_item_id)) || r)
    await onReorder(location, next, changed)
  }

  const onDragStart = (e, id) => {
    e.stopPropagation()
    setDragId(id)
    e.dataTransfer.effectAllowed = 'move'
    try {
      e.dataTransfer.setData('text/plain', String(id))
      e.dataTransfer.setData('text', String(id))
    } catch (_) {
      /* some browsers restrict setData outside dragstart */
    }
  }

  const onDragOver = (e, row) => {
    e.preventDefault()
    e.stopPropagation()
    e.dataTransfer.dropEffect = 'move'
    const id = Number(row.menu_item_id)
    const rect = e.currentTarget.getBoundingClientRect()
    const fromData =
      e.dataTransfer.getData('text/plain') || e.dataTransfer.getData('text')
    const sourceId = Number(fromData || dragId)
    const zone = zoneForTarget(sourceId, row, e.clientY, rect.top, rect.height)
    if (overId !== id) setOverId(id)
    if (overZone !== zone) setOverZone(zone)
  }

  const onDrop = async (e, row) => {
    e.preventDefault()
    e.stopPropagation()
    const fromData =
      e.dataTransfer.getData('text/plain') || e.dataTransfer.getData('text')
    const sourceId = Number(fromData || dragId)
    const targetId = Number(row.menu_item_id)
    const rect = e.currentTarget.getBoundingClientRect()
    const zone =
      zoneForTarget(sourceId, row, e.clientY, rect.top, rect.height) || overZone || 'after'
    setDragId(null)
    setOverId(null)
    setOverZone(null)
    await applyDrop(sourceId, targetId, zone)
  }

  const onDragEnd = () => {
    setDragId(null)
    setOverId(null)
    setOverZone(null)
  }

  return (
    <div className="manage-card mb-4">
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap">
        <h2 className="h5 mb-0">{title}</h2>
        {savingOrder ? (
          <span className="text-muted small">Saving order…</span>
        ) : (
          <span className="text-muted small">
            Drag onto a menu to nest under it, or drop above/below to reorder (autosaves)
          </span>
        )}
      </div>

      {displayItems.length === 0 ? (
        <p className="text-muted mb-0">No items yet. Add a link below.</p>
      ) : (
        <ul className="manage-menu-dnd manage-menu-dnd-list list-unstyled mb-0">
          {displayItems.map((row) => {
            const id = Number(row.menu_item_id)
            const child = !isTopLevel(row)
            const dragging = dragId === id
            const over = overId === id && dragId !== id
            const zone = over ? overZone : null
            const childCount = countChildren(items, id)
            const group = groupFor(items, parentKeyOf(row))
            const idxInGroup = group.findIndex((r) => Number(r.menu_item_id) === id)
            const canUp = idxInGroup > 0
            const canDown = idxInGroup >= 0 && idxInGroup < group.length - 1
            return (
              <li
                key={id}
                className={`manage-menu-dnd-item${child ? ' is-child' : ''}${
                  dragging ? ' is-dragging' : ''
                }${over && zone === 'into' ? ' is-over-into' : ''}${
                  over && zone === 'before' ? ' is-over-before' : ''
                }${over && zone === 'after' ? ' is-over-after' : ''}`}
                onDragOver={(e) => onDragOver(e, row)}
                onDrop={(e) => onDrop(e, row)}
                onDragEnd={onDragEnd}
              >
                <span
                  className="manage-menu-dnd-handle"
                  title="Drag onto another menu to nest, or above/below to reorder"
                  aria-label="Drag to move or nest"
                  role="button"
                  tabIndex={0}
                  draggable={!savingOrder}
                  onDragStart={(e) => onDragStart(e, id)}
                >
                  ⋮⋮
                </span>
                <div className="manage-menu-dnd-move">
                  <button
                    type="button"
                    className="btn btn-sm btn-light manage-menu-move-btn"
                    title="Move up"
                    disabled={!canUp || savingOrder}
                    onClick={() => moveByOffset(row, -1)}
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-light manage-menu-move-btn"
                    title="Move down"
                    disabled={!canDown || savingOrder}
                    onClick={() => moveByOffset(row, 1)}
                  >
                    ↓
                  </button>
                </div>
                <div className="manage-menu-dnd-body">
                  <div className="manage-menu-dnd-label">
                    {child ? (
                      <span className="manage-menu-nest-mark" aria-hidden>
                        └
                      </span>
                    ) : null}
                    {row.label}
                    {childCount > 0 ? (
                      <span className="manage-menu-badge manage-menu-badge-dd">
                        Dropdown ({childCount})
                      </span>
                    ) : null}
                    {zone === 'into' ? (
                      <span className="manage-menu-badge manage-menu-badge-dd">
                        Drop to nest under this menu
                      </span>
                    ) : null}
                    {child ? (
                      <span className="manage-menu-badge">Submenu</span>
                    ) : null}
                    {child && labelById[Number(row.parent_id)] ? (
                      <span className="manage-menu-badge">
                        under {labelById[Number(row.parent_id)]}
                      </span>
                    ) : null}
                    {Number(row.status) !== 1 ? (
                      <span className="manage-menu-badge">Inactive</span>
                    ) : null}
                    {Number(row.open_new_tab) === 1 ? (
                      <span className="manage-menu-badge manage-menu-badge-tab">New tab</span>
                    ) : null}
                  </div>
                  <div className="manage-menu-dnd-url">{row.url || '#'}</div>
                </div>
                <div className="manage-menu-dnd-actions">
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-primary mr-2"
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      onEdit(row)
                    }}
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger"
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      onDelete(row.menu_item_id)
                    }}
                  >
                    Delete
                  </button>
                </div>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}

function MenusPage() {
  const [headerItems, setHeaderItems] = useState([])
  const [footerItems, setFooterItems] = useState([])
  const [error, setError] = useState('')
  const [message, setMessage] = useState(null)
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [saving, setSaving] = useState(false)
  const [savingOrder, setSavingOrder] = useState(false)
  const [form, setForm] = useState(EMPTY)
  const [formError, setFormError] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    const data = await GetAction('get', { all: 1 }, 'menu/list')
    if (data?.status === 'success' && Array.isArray(data.response)) {
      const rows = data.response
      setHeaderItems(
        rows
          .filter((r) => r.location === 'header')
          .sort(sortByOrder)
      )
      setFooterItems(
        rows
          .filter((r) => r.location === 'footer')
          .sort(sortByOrder)
      )
      setError('')
    } else {
      setError(data?.message || 'Failed to load menus.')
      setHeaderItems([])
      setFooterItems([])
    }
    setLoading(false)
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const editingHasChildren = useMemo(() => {
    if (!form.menu_item_id) return false
    const pool = form.location === 'footer' ? footerItems : headerItems
    return countChildren(pool, form.menu_item_id) > 0
  }, [form.menu_item_id, form.location, headerItems, footerItems])

  const parentOptions = useMemo(() => {
    const pool = form.location === 'footer' ? footerItems : headerItems
    return pool.filter((r) => {
      if (!isTopLevel(r)) return false
      if (form.menu_item_id && Number(r.menu_item_id) === Number(form.menu_item_id)) {
        return false
      }
      return true
    })
  }, [form.location, form.menu_item_id, headerItems, footerItems])

  const closeForm = () => {
    setShowForm(false)
    setFormError('')
  }

  const openNew = (location) => {
    setForm({ ...EMPTY, location })
    setFormError('')
    setShowForm(true)
    setMessage(null)
  }

  const openEdit = (row) => {
    setForm({
      menu_item_id: row.menu_item_id != null ? String(row.menu_item_id) : '',
      location: row.location || 'header',
      parent_id:
        row.parent_id != null && Number(row.parent_id) > 0 ? String(row.parent_id) : '',
      label: row.label || '',
      url: row.url != null ? String(row.url) : '',
      sort_order: row.sort_order ?? 0,
      status: Number(row.status) === 1 ? 1 : 0,
      open_new_tab: Number(row.open_new_tab) === 1 ? 1 : 0,
    })
    setFormError('')
    setShowForm(true)
    setMessage(null)
  }

  const onSubmit = async (e) => {
    e.preventDefault()
    if (!form.label.trim()) {
      setFormError('Label is required.')
      return
    }
    if (editingHasChildren && form.parent_id) {
      setFormError(
        'Move or remove child items before nesting this dropdown under another item.'
      )
      return
    }
    setSaving(true)
    setFormError('')
    setMessage(null)
    const payload = {
      menu_item_id: form.menu_item_id ? Number(form.menu_item_id) : undefined,
      location: form.location,
      parent_id:
        !editingHasChildren && form.parent_id ? Number(form.parent_id) : null,
      label: form.label.trim(),
      url: (form.url || '').trim() || '#',
      sort_order: Number(form.sort_order) || 0,
      status: Number(form.status) === 1 ? 1 : 0,
      open_new_tab: Number(form.open_new_tab) === 1 ? 1 : 0,
    }
    const data = await PostAction('post', payload, 'menu/save')
    if (data?.status === 'success') {
      setMessage({ error: false, text: data.message || 'Menu item saved.' })
      setShowForm(false)
      await load()
    } else {
      setFormError(data?.message || 'Failed to save menu item.')
    }
    setSaving(false)
  }

  const onDelete = async (id) => {
    if (
      !window.confirm(
        'Delete this menu item? Child items (if any) become top-level links.'
      )
    ) {
      return
    }
    const data = await PostAction('post', { menu_item_id: id }, 'menu/delete')
    setMessage({
      error: data?.status !== 'success',
      text: data?.message || (data?.status === 'success' ? 'Deleted.' : 'Failed.'),
    })
    if (data?.status === 'success') await load()
  }

  const onReorder = async (location, nextItems, changedItems) => {
    const setItems = location === 'header' ? setHeaderItems : setFooterItems
    setItems(nextItems)

    setSavingOrder(true)
    const data = await PostAction(
      'post',
      {
        location,
        items: changedItems.map((row) => ({
          menu_item_id: row.menu_item_id,
          sort_order: row.sort_order,
          parent_id: row.parent_id == null ? null : row.parent_id,
        })),
      },
      'menu/reorder'
    )
    setSavingOrder(false)
    if (data?.status === 'success') {
      const nested = changedItems.some((row) => parentKeyOf(row) != null)
      setMessage({
        error: false,
        text: nested ? 'Menu nest and order saved.' : 'Menu order saved.',
      })
    } else {
      setMessage({ error: true, text: data?.message || 'Failed to save order.' })
      await load()
    }
  }

  const isEditing = Boolean(form.menu_item_id)

  return (
    <RequireModule moduleKey="menus">
      <PageHeader
        title="Menus"
        subtitle="Configure header and footer links. Drag a menu onto another to nest it as a submenu, or drop above/below to reorder."
        actions={
          <div className="d-flex flex-wrap">
            <button
              type="button"
              className="btn btn-sm btn-primary mr-2"
              style={BTN_PRIMARY}
              onClick={() => openNew('header')}
            >
              Add Header Link
            </button>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={() => openNew('footer')}
            >
              Add Footer Link
            </button>
          </div>
        }
      />

      {message ? (
        <div
          className={`manage-alert ${message.error ? 'manage-alert-err' : 'manage-alert-ok'}`}
        >
          {message.text}
        </div>
      ) : null}
      {error ? <div className="manage-alert manage-alert-err">{error}</div> : null}

      {loading ? (
        <p className="text-muted">Loading…</p>
      ) : (
        <>
          <MenuList
            location="header"
            title="Header menu"
            items={headerItems}
            onEdit={openEdit}
            onDelete={onDelete}
            onReorder={onReorder}
            savingOrder={savingOrder}
          />
          <MenuList
            location="footer"
            title="Footer menu"
            items={footerItems}
            onEdit={openEdit}
            onDelete={onDelete}
            onReorder={onReorder}
            savingOrder={savingOrder}
          />
        </>
      )}

      <Modal
        show={showForm}
        onHide={closeForm}
        backdrop="static"
        centered
        scrollable
      >
        <Modal.Header closeButton>
          <Modal.Title>{isEditing ? 'Edit Menu Item' : 'Add Menu Item'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {formError ? (
            <div className="manage-alert manage-alert-err mb-3">{formError}</div>
          ) : null}
          <form id="manage-menu-form" onSubmit={onSubmit}>
            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label" htmlFor="m_loc">
                  Location
                </label>
                <select
                  id="m_loc"
                  className="form-control"
                  value={form.location}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      location: e.target.value,
                      parent_id: '',
                    }))
                  }
                >
                  <option value="header">Header</option>
                  <option value="footer">Footer</option>
                </select>
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label" htmlFor="m_parent">
                  Parent (dropdown)
                </label>
                <select
                  id="m_parent"
                  className="form-control"
                  value={form.parent_id}
                  disabled={editingHasChildren}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, parent_id: e.target.value }))
                  }
                >
                  <option value="">— Top level (no parent) —</option>
                  {!editingHasChildren
                    ? parentOptions.map((opt) => (
                        <option
                          key={opt.menu_item_id}
                          value={String(opt.menu_item_id)}
                        >
                          {opt.label}
                        </option>
                      ))
                    : null}
                </select>
                <small className="form-text text-muted">
                  {editingHasChildren
                    ? 'This item has children, so it must stay a top-level dropdown parent.'
                    : 'Choose a parent, or drag this item onto another menu in the list to nest it. Leave empty for a main menu item.'}
                </small>
              </div>
              <div className="col-md-12 mb-3">
                <label className="form-label" htmlFor="m_label">
                  Label
                </label>
                <input
                  id="m_label"
                  className="form-control"
                  value={form.label}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, label: e.target.value }))
                  }
                  required
                />
              </div>
              <div className="col-md-12 mb-3">
                <label className="form-label" htmlFor="m_url">
                  Link / URL
                </label>
                <input
                  id="m_url"
                  className="form-control"
                  placeholder="/Donation or https://… (use # for dropdown parent only)"
                  value={form.url}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, url: e.target.value }))
                  }
                />
                <small className="form-text text-muted">
                  For a dropdown title with no page of its own, use <code>#</code>.
                </small>
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label" htmlFor="m_status">
                  Status
                </label>
                <select
                  id="m_status"
                  className="form-control"
                  value={form.status}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, status: Number(e.target.value) }))
                  }
                >
                  <option value={1}>Active</option>
                  <option value={0}>Inactive</option>
                </select>
              </div>
              <div className="col-md-6 mb-3 d-flex align-items-end">
                <label className="form-check mb-2">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    checked={Number(form.open_new_tab) === 1}
                    onChange={(e) =>
                      setForm((p) => ({
                        ...p,
                        open_new_tab: e.target.checked ? 1 : 0,
                      }))
                    }
                  />
                  <span className="form-check-label">Open in new tab</span>
                </label>
              </div>
            </div>
          </form>
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={closeForm}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="manage-menu-form"
            className="btn btn-primary"
            style={BTN_PRIMARY}
            disabled={saving}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </Modal.Footer>
      </Modal>
    </RequireModule>
  )
}

export default MenusPage
